import { forwardRef, useImperativeHandle, useState, useMemo } from 'react';
import { Form, message, Modal, Spin, Typography } from "antd";
import { useRequest } from "ahooks";
import { EditForm, FormConfigType } from "../../../components/editForm";
import { langCommon } from "../../../locales";
import { CommonServer } from "../../../server";

const server = CommonServer.Mine;
const { Paragraph } = Typography;

export type GoogleAuthModeType = 'bind' | 'reset'

const DEFAULT_OPERATION_DATA = {
    VerifyCode: "",
    Password: "",
};

const DEFAULT_SECRET_INFO = {
    Base64Img: "",
    GoogleSecret: "",
};

//接口返回的二维码可能不带 data 前缀
const formatBase64Img = (img: string) => {
    if (!img) return '';
    return img.indexOf('data:') === 0 ? img : `data:image/png;base64,${img}`;
}

export const GoogleAuth = forwardRef((props: any, ref: any) => {
    const { onSuccess } = props;
    const [loading, setLoading] = useState<boolean>(false);
    const [visible, setVisiable] = useState<boolean>(false);
    const [mode, setMode] = useState<GoogleAuthModeType>('bind');
    const [secretInfo, setSecretInfo] = useState<any>({ ...DEFAULT_SECRET_INFO });
    const [formData, setFormData] = useState<any>({ ...DEFAULT_OPERATION_DATA });
    const [form] = Form.useForm();

    useImperativeHandle(ref, () => ({
        changeVisible
    }))

    //获取绑定用的二维码与密钥
    const { run: getSecret } = useRequest(server.bindGoogleAuth, {
        manual: true,
        onBefore: () => setLoading(true),
        onFinally: () => setLoading(false),
        onError: () => setSecretInfo({ ...DEFAULT_SECRET_INFO }),
        onSuccess: (res: any) => {
            setSecretInfo({
                Base64Img: res?.Base64Img || '',
                GoogleSecret: res?.GoogleSecret || '',
            });
        }
    });

    const changeVisible = (val: any, mode?: GoogleAuthModeType) => {
        if (val) {
            mode && setMode(mode);
            form?.resetFields?.();
            setFormData({ ...DEFAULT_OPERATION_DATA });
            setSecretInfo({ ...DEFAULT_SECRET_INFO });
            if (mode !== 'reset') {
                getSecret();
            }
        }
        setVisiable(!!val);
    }

    const { run } = useRequest(mode === 'reset' ? server.resetGoogleAuth : server.submitGoogleAuth, {
        manual: true,
        onBefore: () => setLoading(true),
        onFinally: () => setLoading(false),
        onSuccess: () => {
            message.success(mode === 'reset' ? '重置成功，请重新绑定谷歌验证器' : '绑定成功');
            changeVisible(false);
            onSuccess?.();
        }
    });

    const getFormConfig: FormConfigType = useMemo((): FormConfigType => {
        return mode === 'reset' ? [
            {
                title: '登录密码',
                type: "input",
                extendType: 'Password',
                field: "Password",
                allowClear: true,
                rules: [{
                    required: true,
                    message: `${langCommon?.pleaseInput}登录密码`
                }]
            },
        ] : [
            {
                title: '验证码',
                type: "input",
                field: "VerifyCode",
                maxLength: 6,
                allowClear: true,
                rules: [{
                    required: true,
                    validator: async (rule: any, value: any) => {
                        if (!value) {
                            return Promise.reject(`${langCommon?.pleaseInput}谷歌验证码`);
                        } else if (!/^\d{6}$/.test(value)) {
                            return Promise.reject(`请输入6位数字验证码`);
                        }
                        return Promise.resolve();
                    }
                }]
            },
        ];
    }, [mode]);

    return (
        <div>
            <Modal
                title={mode === 'reset' ? '重置谷歌验证器' : '绑定谷歌验证器'}
                width={460}
                closeIcon={null}
                maskClosable={false}
                open={visible}
                onCancel={() => changeVisible(false)}
                okButtonProps={{
                    loading,
                    disabled: mode !== 'reset' && !secretInfo.GoogleSecret
                }}
                onOk={async () => {
                    await form.validateFields();
                    run(mode === 'reset'
                        ? { Password: formData.Password }
                        : { GoogleSecret: secretInfo.GoogleSecret, VerifyCode: formData.VerifyCode }
                    );
                }}
            >
                {mode === 'reset' ? (
                    <div className="mt20">重置后当前的谷歌验证器将失效，需要重新绑定。</div>
                ) : (
                    <Spin spinning={loading && !secretInfo.GoogleSecret}>
                        <div className="mt20 flex flex-column align-center">
                            {secretInfo.Base64Img ? (
                                <img
                                    src={formatBase64Img(secretInfo.Base64Img)}
                                    alt="谷歌验证器二维码"
                                    style={{ width: 160, height: 160 }}
                                />
                            ) : null}
                            <div className="mt10">请使用谷歌验证器 App 扫码，或手动添加下方密钥</div>
                            <Paragraph className="mt10" copyable={!!secretInfo.GoogleSecret}>
                                {secretInfo.GoogleSecret || '-'}
                            </Paragraph>
                        </div>
                    </Spin>
                )}
                <EditForm
                    componentId="manager-google-auth"
                    className="mt20"
                    form={form}
                    formAttrsProps={{
                        labelCol: { span: 6 },
                        wrapperCol: { span: 16 },
                    }}
                    formData={formData}
                    onDataChange={(data: any) => {
                        setFormData(data);
                    }}
                    formConfig={getFormConfig}
                />
            </Modal>
        </div>
    );
})
